import { useEffect, useRef, useCallback } from 'react';
import { CallMeshService } from '@/services/callMeshService';
import { useCallStore } from '@/store/callStore';
import { useRoomStore } from '@/store/roomStore';
import type { SignalingMessage, CallStatePayload, CallSignalPayload } from '@/types';

interface UseCallMeshOptions {
  participantId: string | null;
  send: (msg: Omit<SignalingMessage, 'timestamp'>) => void;
  on: (type: SignalingMessage['type'], handler: (msg: SignalingMessage) => void) => () => void;
}

export function useCallMesh({ participantId, send, on }: UseCallMeshOptions) {
  const meshRef = useRef<CallMeshService | null>(null);

  const {
    localMicEnabled,
    localCamEnabled,
    localStream,
    isConnectingMedia,
    callError,
    remoteStreams,
    peerMediaStates,
    setLocalMicEnabled,
    setLocalCamEnabled,
    setLocalStream,
    setIsConnectingMedia,
    setCallError,
    setRemoteStream,
    removeRemoteStream,
    setPeerMediaState,
    removePeerMediaState,
    resetCallStore,
  } = useCallStore();

  const { room, updateRoom } = useRoomStore();

  // Initialize CallMeshService
  useEffect(() => {
    if (!participantId) return;

    const mesh = new CallMeshService(participantId, {
      onRemoteStream: (peerId, stream) => {
        setRemoteStream(peerId, stream);
      },
      onRemoteStreamRemoved: (peerId) => {
        removeRemoteStream(peerId);
      },
      sendSignal: (type, targetId, payload) => {
        send({
          type,
          from: participantId,
          to: targetId,
          payload,
        });
      },
    });

    meshRef.current = mesh;

    return () => {
      mesh.close();
      meshRef.current = null;
      resetCallStore();
    };
  }, [participantId, send, setRemoteStream, removeRemoteStream, resetCallStore]);

  // Wire signaling listeners
  useEffect(() => {
    const offCallState = on('CALL_STATE', (msg: SignalingMessage) => {
      const payload = msg.payload as CallStatePayload;
      if (!payload?.participantId) return;

      setPeerMediaState(payload.participantId, {
        micEnabled: payload.micEnabled,
        camEnabled: payload.camEnabled,
      });

      // Also update room participants in roomStore for ParticipantList
      if (room) {
        const updated = room.participants.map((p) =>
          p.id === payload.participantId
            ? { ...p, micEnabled: payload.micEnabled, camEnabled: payload.camEnabled }
            : p
        );
        updateRoom({ participants: updated });
      }

      // If remote peer turned on audio or video, ensure peer node is ready
      if (meshRef.current && (payload.micEnabled || payload.camEnabled)) {
        meshRef.current.getOrCreatePeer(payload.participantId);
      }
    });

    const offCallOffer = on('CALL_OFFER', async (msg: SignalingMessage) => {
      if (!meshRef.current || !msg.from) return;
      const payload = msg.payload as CallSignalPayload;
      if (payload?.sdp) {
        await meshRef.current.handleOffer(msg.from, payload.sdp);
      }
    });

    const offCallAnswer = on('CALL_ANSWER', async (msg: SignalingMessage) => {
      if (!meshRef.current || !msg.from) return;
      const payload = msg.payload as CallSignalPayload;
      if (payload?.sdp) {
        await meshRef.current.handleAnswer(msg.from, payload.sdp);
      }
    });

    const offCallIce = on('CALL_ICE_CANDIDATE', async (msg: SignalingMessage) => {
      if (!meshRef.current || !msg.from) return;
      const payload = msg.payload as CallSignalPayload;
      if (payload?.candidate) {
        await meshRef.current.handleIceCandidate(msg.from, payload.candidate);
      }
    });

    const offParticipantLeft = on('PARTICIPANT_LEFT', (msg: SignalingMessage) => {
      const payload = msg.payload as { participantId: string };
      if (payload?.participantId) {
        meshRef.current?.removePeer(payload.participantId);
        removeRemoteStream(payload.participantId);
        removePeerMediaState(payload.participantId);
      }
    });

    return () => {
      offCallState();
      offCallOffer();
      offCallAnswer();
      offCallIce();
      offParticipantLeft();
    };
  }, [
    on,
    room,
    updateRoom,
    setPeerMediaState,
    removeRemoteStream,
    removePeerMediaState,
  ]);

  // Toggle Microphone
  const toggleMicrophone = useCallback(async () => {
    if (!meshRef.current) return;
    setCallError(null);

    if (localMicEnabled) {
      // Turn off
      meshRef.current.disableMicrophone();
      setLocalMicEnabled(false);
      setLocalStream(meshRef.current.getLocalStream());
    } else {
      // Turn on
      setIsConnectingMedia(true);
      try {
        await meshRef.current.enableMicrophone();
        setLocalMicEnabled(true);
        setLocalStream(meshRef.current.getLocalStream());
      } catch (err: unknown) {
        const error = err as Error;
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setCallError('Microphone permission was denied in your browser.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setCallError('No microphone hardware was detected on this device.');
        } else {
          setCallError('Unable to access microphone: ' + (error.message || 'unknown error'));
        }
      } finally {
        setIsConnectingMedia(false);
      }
    }
  }, [localMicEnabled, setLocalMicEnabled, setLocalStream, setIsConnectingMedia, setCallError]);

  // Toggle Camera
  const toggleCamera = useCallback(async () => {
    if (!meshRef.current) return;
    setCallError(null);

    if (localCamEnabled) {
      // Turn off
      meshRef.current.disableCamera();
      setLocalCamEnabled(false);
      setLocalStream(meshRef.current.getLocalStream());
    } else {
      // Turn on
      setIsConnectingMedia(true);
      try {
        await meshRef.current.enableCamera();
        setLocalCamEnabled(true);
        setLocalStream(meshRef.current.getLocalStream());
      } catch (err: unknown) {
        const error = err as Error;
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setCallError('Camera permission was denied in your browser.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setCallError('No camera hardware was detected on this device.');
        } else {
          setCallError('Unable to access camera: ' + (error.message || 'unknown error'));
        }
      } finally {
        setIsConnectingMedia(false);
      }
    }
  }, [localCamEnabled, setLocalCamEnabled, setLocalStream, setIsConnectingMedia, setCallError]);

  return {
    localMicEnabled,
    localCamEnabled,
    localStream,
    isConnectingMedia,
    callError,
    remoteStreams,
    peerMediaStates,
    toggleMicrophone,
    toggleCamera,
    clearCallError: () => setCallError(null),
  };
}

